import type {
  MemoryRecordType,
  MemorySort,
  MemoryView,
  ProcessingStatus,
} from '../../api/resources/memoryTypes'

export interface MemorySearch {
  view?: MemoryView | undefined
  record?: string | undefined
  section?: 'signals' | 'evidence' | 'details' | undefined
  q?: string | undefined
  area?: string | undefined
  visibility?: 'private' | 'workspace' | undefined
  status?: ProcessingStatus | undefined
  sort?: MemorySort | undefined
  offset?: number | undefined
  packet?: string | undefined
  revision?: number | undefined
  version?: string | undefined
  start?: number | undefined
  end?: number | undefined
}
export type MemoryReference = Pick<MemorySearch, 'revision' | 'version' | 'start' | 'end'>
export type OpenMemoryRecord = (
  record: string,
  section?: MemorySearch['section'],
  reference?: MemoryReference,
) => void
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export function parseMemoryRecord(
  value: unknown,
): { type: MemoryRecordType; id: string } | null {
  if (typeof value !== 'string') return null
  const [type, id, extra] = value.split(':')
  if (extra !== undefined) return null
  return (type === 'source' || type === 'evidence' || type === 'signal') && id && uuid.test(id)
    ? { type, id }
    : null
}
export function validateMemorySearch(raw: Record<string, unknown>): MemorySearch {
  const search: MemorySearch = {}
  if (
    typeof raw.view === 'string' &&
    ['inbox', 'evidence', 'signals', 'sources'].includes(raw.view)
  )
    search.view = raw.view as MemoryView
  if (parseMemoryRecord(raw.record)) search.record = String(raw.record)
  if (
    typeof raw.section === 'string' &&
    ['signals', 'evidence', 'details'].includes(raw.section)
  )
    search.section = raw.section as MemorySearch['section']
  if (typeof raw.q === 'string' && raw.q.trim()) search.q = raw.q.slice(0, 500)
  if (typeof raw.area === 'string' && uuid.test(raw.area)) search.area = raw.area
  if (raw.visibility === 'private' || raw.visibility === 'workspace')
    search.visibility = raw.visibility
  if (
    typeof raw.status === 'string' &&
    ['awaiting_upload', 'queued', 'processing', 'ready', 'partial', 'failed'].includes(
      raw.status,
    )
  )
    search.status = raw.status as ProcessingStatus
  if (raw.sort === 'oldest' || raw.sort === 'az') search.sort = raw.sort
  if (validInteger(raw.offset) && Number(raw.offset) > 0) search.offset = Number(raw.offset)
  if (typeof raw.packet === 'string' && uuid.test(raw.packet)) search.packet = raw.packet
  if (validInteger(raw.revision) && Number(raw.revision) > 0)
    search.revision = Number(raw.revision)
  if (typeof raw.version === 'string' && uuid.test(raw.version)) search.version = raw.version
  if (validInteger(raw.start)) search.start = Number(raw.start)
  if (validInteger(raw.end) && Number(raw.end) > (search.start ?? 0))
    search.end = Number(raw.end)
  return search
}

export function memoryHref(workspaceId: string, search: MemorySearch) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(search))
    if (value !== undefined && value !== '') params.set(key, String(value))
  return `/w/${encodeURIComponent(workspaceId)}/memory${params.size ? `?${params}` : ''}`
}

function validInteger(value: unknown) {
  return (
    (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) &&
    Number.isSafeInteger(Number(value)) &&
    Number(value) >= 0
  )
}
